import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Table.
 *
 * The most consequential component in an accounting tool — most of what people
 * do here is read columns of numbers and compare them.
 *
 * ── DESIGN DECISIONS THAT MATTER ──────────────────────────────────────────
 *
 * 1. A real <table>. Not divs with `role="table"`. Screen readers announce
 *    "column 4 of 9, Amount, 1,240.00" only for genuine table semantics, and
 *    without that a blind accountant cannot use a ledger at all.
 *
 * 2. `align="right"` on a cell also turns on tabular figures. The two always go
 *    together for money and are separable only by mistake, so the component
 *    couples them rather than trusting every caller to remember `tnum`.
 *
 * 3. Row separators are the SUBTLE border, not the default one. At forty rows
 *    a visible rule per row turns the table into a grid and the numbers stop
 *    being the thing you see.
 *
 * 4. The header sticks. A ledger runs past a screenful constantly, and a
 *    scrolled-away header means counting columns to work out which figure is
 *    the debit.
 */

/** Wraps the table so wide content scrolls inside the card, not the page. */
export function TableWrap({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('w-full overflow-x-auto', className)} {...rest}>
      {children}
    </div>
  );
}

export function Table({ className, children, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return (
    <table className={cn('w-full border-collapse text-md', className)} {...rest}>
      {children}
    </table>
  );
}

export function TableHead({
  className,
  sticky = true,
  children,
  ...rest
}: HTMLAttributes<HTMLTableSectionElement> & { sticky?: boolean }) {
  return (
    <thead
      className={cn(
        'bg-(--table-header-bg)',
        // z-index keeps the header above cell content while scrolling. Named
        // token rather than a magic number.
        sticky && 'sticky top-0 z-(--p-z-sticky)',
        className,
      )}
      {...rest}
    >
      {children}
    </thead>
  );
}

export function TableBody({ className, children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={className} {...rest}>
      {children}
    </tbody>
  );
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Highlights the row as chosen. Distinct from hover, so both can be true. */
  selected?: boolean;
  /** Adds hover feedback and a pointer. Only pass when the row really is clickable. */
  interactive?: boolean;
  /** Dims the row — an archived account, a superseded entry. */
  muted?: boolean;
}

export function TableRow({
  className,
  selected = false,
  interactive = false,
  muted = false,
  children,
  ...rest
}: TableRowProps) {
  return (
    <tr
      // Announced by screen readers, and it is the hook the selected style
      // hangs off — so the visual and the semantic can never disagree.
      aria-selected={selected || undefined}
      className={cn(
        'border-b border-(--table-border) last:border-b-0',
        'transition-colors duration-(--m-instant)',
        interactive && 'cursor-pointer hover:bg-hover',
        selected && 'bg-selected',
        muted && 'opacity-55',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

type Align = 'left' | 'right' | 'center';

const ALIGN: Record<Align, string> = {
  left: 'text-left',
  // Numeric columns get tabular figures automatically — see note 2 above.
  right: 'text-right tnum',
  center: 'text-center',
};

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: Align;
  /** Stops long values wrapping. Right for codes and amounts, wrong for prose. */
  nowrap?: boolean;
}

export function TableCell({
  className,
  align = 'left',
  nowrap = false,
  children,
  ...rest
}: TableCellProps) {
  return (
    <td
      className={cn(
        'h-(--table-row-height) px-(--table-cell-padding-x) py-(--table-cell-padding-y)',
        'text-primary align-middle',
        ALIGN[align],
        nowrap && 'whitespace-nowrap',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

export interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: Align;
  /**
   * Current sort direction, or 'none' when this column is sortable but not the
   * one being sorted. Omit entirely for columns that cannot be sorted —
   * `aria-sort` on a non-sortable column tells a screen reader user a control
   * exists that does not.
   */
  sort?: 'asc' | 'desc' | 'none';
}

export function TableHeaderCell({
  className,
  align = 'left',
  sort,
  children,
  ...rest
}: TableHeaderCellProps) {
  return (
    <th
      scope="col"
      aria-sort={sort === 'asc' ? 'ascending' : sort === 'desc' ? 'descending' : sort === 'none' ? 'none' : undefined}
      className={cn(
        'h-(--table-header-height) px-(--table-cell-padding-x)',
        'border-b border-(--table-border)',
        // The uppercase letterspaced micro-label both references use for column
        // headers. Kept quiet so the values below carry the weight.
        'text-2xs font-semibold uppercase tracking-caps text-(--table-header-fg)',
        'whitespace-nowrap select-none',
        ALIGN[align],
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

